/**
 * Where sheet bundles come from. Default: stream from the network (/api/media, checked for X-KGR).
 * Milestone 5 (offline packs / recent LRU) installs a resolver with setSheetBundleSource() that
 * answers from the Cache API first (a cached Response streams exactly like a network one) and may
 * tee network responses into its cache. Returning null falls back to the network.
 */
import type { SheetTier } from '../../shared/dto/library';
import { mediaUrl } from '../../shared/dto/offline';
import { apiRaw } from '../api/client';

export interface BundleRequest {
  fileId: string;
  rev: number;
  tier: SheetTier;
  /** Same-origin media URL, e.g. /api/media/<id>/<rev>/m */
  url: string;
  signal: AbortSignal;
}

export type BundleSource = (req: BundleRequest) => Promise<Response | Blob | null>;

let resolver: BundleSource | null = null;

export function setSheetBundleSource(fn: BundleSource | null): void {
  resolver = fn;
}

/** Bundles can be tens of MB on slow choir-hall Wi-Fi: allow a long transfer. */
const BUNDLE_TIMEOUT_MS = 10 * 60_000;

export async function openBundle(fileId: string, rev: number, tier: SheetTier, signal: AbortSignal): Promise<Response | Blob> {
  const url = mediaUrl(fileId, rev, tier);
  if (resolver) {
    const r = await resolver({ fileId, rev, tier, url, signal });
    if (r) return r;
  }
  return apiRaw(url.replace(/^\/api/, ''), { signal, timeoutMs: BUNDLE_TIMEOUT_MS });
}
